import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { Area, CreateArea, UpdateArea } from '@estimator/shared';

export function useAreas(projectId: string) {
  return useQuery({
    queryKey: ['areas', projectId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ areas: Area[] } | Area[]>(
        `/projects/${projectId}/areas`,
      );
      if (Array.isArray(data)) return data;
      return (data as { areas: Area[] }).areas ?? [];
    },
    enabled: !!projectId,
  });
}

export function useCreateArea(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateArea) => {
      const { data } = await apiClient.post<Area>(`/projects/${projectId}/areas`, body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['areas', projectId] }),
  });
}

export function useUpdateArea() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, projectId, ...body }: UpdateArea & { id: string; projectId: string }) => {
      const { data } = await apiClient.patch<Area>(`/projects/${projectId}/areas/${id}`, body);
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['areas', vars.projectId] });
    },
  });
}

export function useDeleteArea() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, projectId }: { id: string; projectId: string }) => {
      await apiClient.delete(`/projects/${projectId}/areas/${id}`);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['areas', vars.projectId] });
    },
  });
}
